import React, { useState } from "react";
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Calendar, Users, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useExperiments } from "@/hooks/useExperiments";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import * as Sentry from '@sentry/react';
import { supabase } from "@/integrations/supabase/client";
import EmptyState from "@/components/EmptyState";
import ExperimentStatusBadge from "@/components/ExperimentStatusBadge";

// Types for local form state
interface LocalExperimentStep {
  id: string;
  day: number;
  description: string;
  productId?: string;
  productName?: string;
}

interface LocalParticipant {
  id: string;
  name: string;
  email: string;
  completedDate: string;
}

interface LocalExperiment {
  id: string;
  name: string;
  startDate: string;
  endDate: string | null;
  status: "active" | "completed" | "draft";
  responders: number;
  totalParticipants: number;
  steps: LocalExperimentStep[];
  participants: LocalParticipant[];
}

// New types for the admin experiment creation
interface AdminExperiment {
  name: string;
  shortcode: string;
}

const Experiments: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { organization } = useOrganization();
  const { data: experiments, isLoading, error, refetch } = useExperiments();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isAdminCreateDialogOpen, setIsAdminCreateDialogOpen] = useState(false);
  const [isCreatingExperiment, setIsCreatingExperiment] = useState(false);

  // Existing state for the old modal
  const [newExperiment, setNewExperiment] = useState<Partial<LocalExperiment>>({
    name: "",
    startDate: new Date().toISOString().split('T')[0],
    endDate: "",
    status: "draft",
    steps: []
  });
  const [newStep, setNewStep] = useState<Partial<LocalExperimentStep>>({
    day: 1,
    description: "",
    productName: ""
  });

  // New state for admin experiment creation
  const [adminExperiment, setAdminExperiment] = useState<AdminExperiment>({
    name: "",
    shortcode: ""
  });

  // Generate shortcode automatically based on experiment name
  const generateShortcode = (name: string) => {
    if (!name || name.length < 2) return "";

    const namePrefix = name.slice(0, 2).toUpperCase();
    const currentDate = new Date();
    const monthPrefix = currentDate.toLocaleString('default', { month: 'short' }).slice(0, 2).toUpperCase();
    const yearSuffix = currentDate.getFullYear().toString().slice(-2);

    return `${namePrefix}${monthPrefix}${yearSuffix}`;
  };

  // Update shortcode when name changes
  const handleNameChange = (name: string) => {
    setAdminExperiment(prev => ({
      ...prev,
      name,
      shortcode: prev.shortcode || generateShortcode(name)
    }));
  };

  const handleRefresh = () => {
    refetch();
  };

  // New function for creating experiments via API
  const handleCreateAdminExperiment = async () => {
    if (!adminExperiment.name) {
      toast.error("Experiment name is required");
      return;
    }

    if (!adminExperiment.shortcode) {
      toast.error("Shortcode is required");
      return;
    }

    if (!organization?.id) {
      toast.error("No organization selected");
      return;
    }

    setIsCreatingExperiment(true);

    try {
      // Call the Tastermonial API to create the experiment
      const { data: apiResponse, error: apiError } = await supabase.functions.invoke('tastermonial-api', {
        body: {
          method: 'POST',
          endpoint: '/api/v2/experiments',
          body: {
            name: adminExperiment.name,
            short_code: adminExperiment.shortcode,
            description: "Give the experiment a short description.",
            status: 'draft'
          }
        },
      });

      if (apiError) {
        throw new Error(apiError.message || 'Failed to create experiment');
      }

      if (apiResponse.status !== 200 && apiResponse.status !== 201) {
        throw new Error(`API error: ${apiResponse.statusText || 'Failed to create experiment'}`);
      }

      const createdExperiment = apiResponse.data;
      console.log('Created experiment:', createdExperiment);

      if (!createdExperiment?.id) {
        throw new Error('No experiment ID returned from API');
      }

      // Connect the experiment to the current organization
      const { error: dbError } = await supabase
        .from('organization_experiments')
        .insert({
          organization_id: organization.id,
          experiment_id: createdExperiment.id,
          reference_name: adminExperiment.name
        });

      if (dbError) {
        Sentry.captureException(dbError);
        throw new Error('Failed to connect experiment to organization');
      }

      // Reset form and close dialog
      setAdminExperiment({ name: "", shortcode: "" });
      setIsAdminCreateDialogOpen(false);
      toast.success("Experiment created successfully");

      // Refresh the experiments list
      refetch();

      // Navigate to the experiment details page
      navigate(`/dashboard/experiments/${createdExperiment.id}`);

    } catch (error: any) {
              Sentry.captureException(error);
      toast.error(error.message || 'Failed to create experiment');
    } finally {
      setIsCreatingExperiment(false);
    }
  };

  // Existing function for the old modal (keeping for compatibility)
  const handleCreateExperiment = () => {
    if (!newExperiment.name) {
      toast.error("Experiment name is required");
      return;
    }

    if (!newExperiment.startDate) {
      toast.error("Start date is required");
      return;
    }

    if (!Array.isArray(newExperiment.steps) || newExperiment.steps.length === 0) {
      toast.error("At least one experiment step is required");
      return;
    }

    const createdExperiment: LocalExperiment = {
      id: `exp-${Date.now()}`,
      name: newExperiment.name || "",
      startDate: newExperiment.startDate || new Date().toISOString().split('T')[0],
      endDate: newExperiment.endDate || null,
      status: "draft",
      responders: 0,
      totalParticipants: 0,
      steps: newExperiment.steps || [],
      participants: []
    };

    // Note: This would need to be updated to use the API
    // setExperiments([...experiments, createdExperiment]);
    setNewExperiment({
      name: "",
      startDate: new Date().toISOString().split('T')[0],
      endDate: "",
      status: "draft",
      steps: []
    });
    setIsCreateDialogOpen(false);
    toast.success("Experiment created successfully");
  };

  const addStep = () => {
    if (!newStep.description) {
      toast.error("Step description is required");
      return;
    }

    const step: LocalExperimentStep = {
      id: `step-${Date.now()}`,
      day: newStep.day || 1,
      description: newStep.description || "",
      productId: newStep.productId || undefined,
      productName: newStep.productName || undefined
    };

    setNewExperiment({
      ...newExperiment,
      steps: [...(newExperiment.steps || []), step]
    });

    setNewStep({
      day: Math.max(...(newExperiment.steps || []).map(s => s.day), 0) + 1,
      description: "",
      productName: ""
    });
  };

  const removeStep = (stepId: string) => {
    setNewExperiment({
      ...newExperiment,
      steps: (newExperiment.steps || []).filter(step => step.id !== stepId)
    });
  };

  const navigateToExperimentDetail = (experimentId: string) => {
    navigate(`/dashboard/experiments/${experimentId}`);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge className="bg-green-500 hover:bg-green-600">Active</Badge>;
      case "completed":
        return <Badge className="bg-blue-500 hover:bg-blue-600">Completed</Badge>;
      case "draft":
        return <Badge className="bg-yellow-500 hover:bg-yellow-600">Draft</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  // Helper function to get unique days count from experiment steps
  const getUniqueDaysCount = (steps: any[]) => {
    if (!steps || steps.length === 0) return 0;
    const uniqueDays = new Set(steps.map(step => step.day));
    return uniqueDays.size;
  };

  const isAdmin = user?.role === 'admin';

  if (error) {
    return (
      <div className="space-y-6">
        <div className="text-center py-6">
          <p className="text-destructive">Error loading experiments: {error.message}</p>
          <Button onClick={() => window.location.reload()} className="mt-4">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Experiments</h1>
          <p className="text-muted-foreground">Create and manage product experiments</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {isAdmin && (
            <Button onClick={() => setIsAdminCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add experiment
            </Button>
          )}
          <Button variant="outline">Export Data</Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0 overflow-auto">
          {isLoading ? (
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-10 w-32" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            </div>
          ) : experiments && experiments.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Days</TableHead>
                  <TableHead>Participants</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {experiments.map((experiment) => (
                  <TableRow
                    key={experiment.id}
                    className="hover:bg-muted/50 cursor-pointer"
                    onClick={() => navigateToExperimentDetail(experiment.id)}
                  >
                    <TableCell className="font-medium">{experiment.name}</TableCell>
                    <TableCell><ExperimentStatusBadge status={experiment.status} /></TableCell>
                    <TableCell>{getUniqueDaysCount(experiment.steps)}</TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <Users className="h-4 w-4 mr-2 text-gray-500" />
                        {experiment.participantsCount !== null && experiment.participantsCount !== undefined ? (
                          <span>{experiment.participantsCount}</span>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <EmptyState
              title="No experiments found"
              description="You don't have any experiments yet. Create your first experiment or refresh to check for updates."
              actionLabel="Refresh"
              onAction={handleRefresh}
            />
          )}
        </CardContent>
      </Card>

      {/* Admin Add Experiment Dialog */}
      <Dialog open={isAdminCreateDialogOpen} onOpenChange={setIsAdminCreateDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Experiment</DialogTitle>
            <DialogDescription>
              Create a new experiment and connect it to your organization.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="admin-name">Experiment Name *</Label>
              <Input
                id="admin-name"
                value={adminExperiment.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="E.g., Biostrips Study"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin-shortcode">Shortcode *</Label>
              <Input
                id="admin-shortcode"
                value={adminExperiment.shortcode}
                onChange={(e) => setAdminExperiment(prev => ({ ...prev, shortcode: e.target.value.toUpperCase() }))}
                placeholder="E.g., BIJU25"
                maxLength={10}
              />
              <p className="text-sm text-muted-foreground">
                Auto-generated based on experiment name and current date. You can modify it.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAdminCreateDialogOpen(false)}
              disabled={isCreatingExperiment}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateAdminExperiment}
              disabled={isCreatingExperiment}
            >
              {isCreatingExperiment ? "Creating..." : "Create Experiment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Existing Create Experiment Dialog (keeping for compatibility) */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Experiment</DialogTitle>
            <DialogDescription>
              Set up a new experiment with steps and products for participants to test.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="name">Experiment Name *</Label>
              <Input
                id="name"
                value={newExperiment.name}
                onChange={(e) => setNewExperiment({...newExperiment, name: e.target.value})}
                placeholder="E.g., Chocolate Satiety Study"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">Start Date *</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={newExperiment.startDate}
                  onChange={(e) => setNewExperiment({...newExperiment, startDate: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">End Date (Optional)</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={newExperiment.endDate || ''}
                  onChange={(e) => setNewExperiment({...newExperiment, endDate: e.target.value || null})}
                />
              </div>
            </div>

            <div>
              <h3 className="font-medium mb-2">Experiment Steps</h3>

              {(newExperiment.steps || []).length > 0 && (
                <div className="mb-4 space-y-2">
                  {(newExperiment.steps || []).map((step, index) => (
                    <div key={step.id} className="flex items-start gap-2 p-3 border rounded-md">
                      <div className="flex-1">
                        <div className="font-medium">Day {step.day}</div>
                        <div className="text-sm">{step.description}</div>
                        {step.productName && (
                          <div className="text-sm text-muted-foreground">Product: {step.productName}</div>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeStep(step.id);
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-3 border rounded-md p-3">
                <div className="grid grid-cols-4 gap-2">
                  <div className="space-y-1">
                    <Label htmlFor="stepDay">Day</Label>
                    <Input
                      id="stepDay"
                      type="number"
                      min="1"
                      value={newStep.day}
                      onChange={(e) => setNewStep({...newStep, day: parseInt(e.target.value)})}
                    />
                  </div>
                  <div className="space-y-1 col-span-3">
                    <Label htmlFor="productName">Product (Optional)</Label>
                    <Input
                      id="productName"
                      value={newStep.productName || ''}
                      onChange={(e) => setNewStep({...newStep, productName: e.target.value})}
                      placeholder="Product name"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="stepDescription">Step Description *</Label>
                  <Textarea
                    id="stepDescription"
                    value={newStep.description}
                    onChange={(e) => setNewStep({...newStep, description: e.target.value})}
                    placeholder="Describe what participants should do in this step"
                    rows={2}
                  />
                </div>

                <Button onClick={addStep} type="button" className="mt-2">Add Step</Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateExperiment}>Create Experiment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Experiments;
